import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { registrarRuta } from "@/components/BotonVolver";
import { NavLink, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { Home, CalendarDays, PlusCircle, Bot, User } from "lucide-react";
import { AddModal } from "@/components/AddModal";
import { Introduccion } from "@/components/intro/Introduccion";
import { BienvenidaInicial } from "@/components/intro/BienvenidaInicial";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Inicio", Icon: Home, testid: "nav-inicio", end: true },
  { to: "/agenda", label: "Agenda", Icon: CalendarDays, testid: "nav-agenda" },
  { to: "/agente", label: "Agente", Icon: Bot, testid: "nav-agente" },
  { to: "/perfil", label: "Perfil", Icon: User, testid: "nav-perfil" },
];

export const Layout = ({ children }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [addFile, setAddFile] = useState(null);
  const [addPersona, setAddPersona] = useState(null);
  const [addOpcion, setAddOpcion] = useState(null);
  const { user, setUser } = useAuth();
  const [intro, setIntro] = useState(false);
  const [bienvenida, setBienvenida] = useState(() => !!(user?.intro_auto && !user?.intro_vista));
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => { registrarRuta(location); }, [location]);
  // Desplazamiento dentro del contenedor: arriba al abrir pantalla; al volver, donde estaba.
  const navType = useNavigationType();
  const scrollRef = useRef(null);
  const posiciones = useRef({});
  const claveRef = useRef(location.key);
  useLayoutEffect(() => {
    claveRef.current = location.key;
    if (scrollRef.current) scrollRef.current.scrollTop = navType === "POP" ? posiciones.current[location.key] || 0 : 0;
  }, [location.key, navType]);
  const guardarPos = (e) => { posiciones.current[claveRef.current] = e.currentTarget.scrollTop; };
  useEffect(() => {
    const abrir = () => setIntro(true);
    const anadir = (e) => { setAddPersona(e?.detail?.persona || null); setAddOpcion(e?.detail?.opcion || null); setAddFile(e?.detail?.file || null); setAddOpen(true); };
    window.addEventListener("aldia-intro", abrir);
    window.addEventListener("aldia-add", anadir);
    return () => { window.removeEventListener("aldia-intro", abrir); window.removeEventListener("aldia-add", anadir); };
  }, []);
  // En móvil, detectar teclado abierto (campo de texto enfocado).
  const [teclado, setTeclado] = useState(false);
  useEffect(() => {
    if (!window.matchMedia("(pointer: coarse)").matches) return;
    const esTexto = (el) => el && (el.tagName === "TEXTAREA" || el.isContentEditable ||
      (el.tagName === "INPUT" && !["checkbox", "radio", "button", "submit", "file", "range", "color"].includes(el.type)));
    const alEnfocar = (e) => setTeclado(esTexto(e.target));
    const alSalir = () => setTimeout(() => setTeclado(esTexto(document.activeElement)), 50);
    document.addEventListener("focusin", alEnfocar);
    document.addEventListener("focusout", alSalir);
    return () => { document.removeEventListener("focusin", alEnfocar); document.removeEventListener("focusout", alSalir); };
  }, []);
  // Con teclado: el contenido ocupa el espacio visible y la barra inferior queda debajo, tapada por el teclado.
  useEffect(() => {
    const vv = window.visualViewport;
    const raiz = document.documentElement.style;
    if (!vv) return;
    const ajustar = () => {
      raiz.setProperty("--app-h", teclado ? `calc(${vv.height}px + 5rem)` : "100dvh");
      if (!teclado) return;
      window.scrollTo(0, 0);
      const el = document.activeElement;
      if (el && el.scrollIntoView) setTimeout(() => el.scrollIntoView({ block: "nearest" }), 50);
    };
    ajustar();
    vv.addEventListener("resize", ajustar);
    return () => vv.removeEventListener("resize", ajustar);
  }, [teclado]);
  const finIntro = (resultado, archivo) => {
    setIntro(false);
    api.post("/auth/intro", { resultado }).then(() => setUser((u) => (u ? { ...u, intro_vista: true } : u))).catch(() => {});
    if (archivo) { setAddFile(archivo); setAddOpen(true); }
  };
  const finBienvenida = (resultado, archivo) => {
    setBienvenida(false);
    finIntro(resultado, archivo);
    if (!archivo) navigate("/");
  };

  return (
    // Altura fija con el contenido desplazable dentro y la barra al final, sin position: fixed (iOS 26 la coloca mal al desplazar).
    <div className="h-screen flex flex-col overflow-hidden bg-[#FAF8F5]" style={{ height: "var(--app-h, 100dvh)" }}>
      <div data-testid="app-scroll" ref={scrollRef} onScroll={guardarPos} className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
        <main className="max-w-2xl md:max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-10">
          {children}
        </main>
      </div>

      {/* Barra de navegación inferior */}
      <nav
        data-testid="bottom-nav"
        data-teclado={teclado ? "abierto" : "cerrado"}
        className={`relative shrink-0 z-40 bg-[#FAF8F5]/95 backdrop-blur-md border-t border-[#E5DFD5]`}
      >
        <div className="max-w-2xl md:max-w-3xl mx-auto px-2 h-20 flex items-center justify-around relative">
          {NAV.slice(0, 2).map(({ to, label, Icon, testid, end }) => (
            <NavItem key={to} to={to} label={label} Icon={Icon} testid={testid} end={end} />
          ))}

          {/* Botón central + Añadir */}
          <button
            data-testid="nav-add-button"
            onClick={() => setAddOpen(true)}
            aria-label="Añadir"
            className="flex flex-col items-center justify-center -mt-8"
          >
            <span className="w-16 h-16 rounded-full bg-[#3A6B56] shadow-lg shadow-[#3A6B56]/30 flex items-center justify-center ring-4 ring-[#FAF8F5] hover:bg-[#2E5645] transition-colors">
              <PlusCircle size={34} className="text-white" strokeWidth={2} />
            </span>
            <span className="text-xs font-semibold text-[#3A6B56] mt-1">Añadir</span>
          </button>

          {NAV.slice(2).map(({ to, label, Icon, testid, end }) => (
            <NavItem key={to} to={to} label={label} Icon={Icon} testid={testid} end={end} />
          ))}
        </div>
      </nav>

      <AddModal open={addOpen} initialFile={addFile} persona={addPersona} opcion={addOpcion} onClose={() => { setAddOpen(false); setAddFile(null); setAddPersona(null); setAddOpcion(null); }} />
      {intro && <Introduccion onFin={finIntro} />}
      {bienvenida && <BienvenidaInicial onFin={finBienvenida} />}
    </div>
  );
};

const NavItem = ({ to, label, Icon, testid, end }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    className="flex flex-col items-center justify-center gap-1 w-16 py-2"
  >
    {({ isActive }) => (
      <>
        <Icon
          size={26}
          strokeWidth={isActive ? 2.4 : 2}
          className={isActive ? "text-[#3A6B56]" : "text-[#9AA39D]"}
        />
        <span
          className={`text-xs font-semibold ${
            isActive ? "text-[#3A6B56]" : "text-[#9AA39D]"
          }`}
        >
          {label}
        </span>
      </>
    )}
  </NavLink>
);
